import { type ComponentType, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Sparkles,
  RefreshCw,
  Info,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  MinusCircle,
  ArrowRight,
  ChevronDown,
  FileText,
  Target,
  Wrench,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import type { UIChecklistItem } from "@/packages/types";
import { AIGeneratePopover } from "./AIGeneratePopover";
import {
  ChecklistGenerationWorkflowCard,
  hasChecklistWorkflowContext,
  shouldShowChecklistWorkflowCard,
} from "./ChecklistGenerationWorkflowCard";

// ─── Skeleton Loader ──────────────────────────────────────────────────────────

function SkeletonLoader({ label }: { label: string }) {
  return (
    <div className="py-5 space-y-4">
      <div className="flex items-center gap-2.5">
        <div className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-accent"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{
                duration: 1.2,
                repeat: Infinity,
                delay: i * 0.2,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
        <span className="text-[12px] text-accent font-medium tracking-wide">
          {label}
        </span>
      </div>
      <div className="space-y-2.5">
        {[90, 72, 85, 55].map((w) => (
          <div
            key={w}
            className="h-2.5 rounded-full shimmer"
            style={{ width: `${w}%` }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Summary Report Types ─────────────────────────────────────────────────────

interface ChecklistSummaryReport {
  complianceStatus: string;
  confidence: number;
  verdict: string;
  keyFindings: Array<{
    finding: string;
    sentiment: "positive" | "concern" | "gap";
  }>;
  gaps: string[];
  nextSteps: string[];
  evidenceSources: string[];
  recommendation: "Approve" | "Request Changes";
}

function parseSummaryReport(
  raw: string,
): ChecklistSummaryReport | string | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      "complianceStatus" in parsed &&
      "confidence" in parsed &&
      "verdict" in parsed &&
      "keyFindings" in parsed
    ) {
      return parsed as ChecklistSummaryReport;
    }
    return raw;
  } catch {
    return raw;
  }
}

// ─── Compliance Status Config ─────────────────────────────────────────────────

type ComplianceLevel = "compliant" | "partial" | "missing" | "non-compliant";

function getComplianceLevel(status: string): ComplianceLevel {
  const s = status.toLowerCase();
  if (s.includes("non") || s.includes("fail")) return "non-compliant";
  if (s.includes("partial")) return "partial";
  if (s.includes("missing")) return "missing";
  if (s.includes("compliant") || s.includes("pass")) return "compliant";
  return "missing";
}

const COMPLIANCE_CONFIG: Record<
  ComplianceLevel,
  {
    icon: ComponentType<{ size?: number; className?: string }>;
    bannerBg: string;
    bannerBorder: string;
    iconColor: string;
    textColor: string;
    barColor: string;
  }
> = {
  compliant: {
    icon: CheckCircle2,
    bannerBg: "bg-green-500/8",
    bannerBorder: "border-green-500/20",
    iconColor: "text-green-600",
    textColor: "text-green-700",
    barColor: "bg-green-500",
  },
  partial: {
    icon: MinusCircle,
    bannerBg: "bg-orange-500/8",
    bannerBorder: "border-orange-500/20",
    iconColor: "text-orange-600",
    textColor: "text-orange-700",
    barColor: "bg-orange-500",
  },
  missing: {
    icon: AlertTriangle,
    bannerBg: "bg-amber-500/8",
    bannerBorder: "border-amber-500/20",
    iconColor: "text-amber-600",
    textColor: "text-amber-700",
    barColor: "bg-amber-500",
  },
  "non-compliant": {
    icon: XCircle,
    bannerBg: "bg-red-500/8",
    bannerBorder: "border-red-500/20",
    iconColor: "text-red-600",
    textColor: "text-red-700",
    barColor: "bg-red-500",
  },
};

// ─── Structured Summary Card ──────────────────────────────────────────────────

const SENTIMENT_CONFIG = {
  positive: {
    icon: CheckCircle2,
    color: "text-green-600",
  },
  concern: {
    icon: AlertTriangle,
    color: "text-amber-600",
  },
  gap: {
    icon: XCircle,
    color: "text-red-600",
  },
} as const;

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">
      {children}
    </span>
  );
}

// ─── Information Report Types ────────────────────────────────────────────────

interface InformationSection {
  key: string;
  title: string;
  content: string;
}

interface ChecklistInformationReport {
  sections: InformationSection[];
}

function parseInformationReport(
  raw: string,
): ChecklistInformationReport | string {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      "sections" in parsed &&
      Array.isArray((parsed as ChecklistInformationReport).sections)
    ) {
      return parsed as ChecklistInformationReport;
    }
    return raw;
  } catch {
    return raw;
  }
}

const INFO_SECTION_CONFIG: Record<
  string,
  {
    icon: ComponentType<{ size?: number; className?: string }>;
    borderColor: string;
    iconBg: string;
    iconColor: string;
  }
> = {
  purpose: {
    icon: Target,
    borderColor: "border-l-blue-500",
    iconBg: "bg-blue-500/10",
    iconColor: "text-blue-600",
  },
  implementation: {
    icon: Wrench,
    borderColor: "border-l-violet-500",
    iconBg: "bg-violet-500/10",
    iconColor: "text-violet-600",
  },
  challenges: {
    icon: AlertTriangle,
    borderColor: "border-l-amber-500",
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-600",
  },
  bestPractices: {
    icon: CheckCircle2,
    borderColor: "border-l-emerald-500",
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-600",
  },
};

function useInfoSectionTitle(key: string, fallback: string): string {
  const { t } = useTranslation("audits");
  switch (key) {
    case "purpose":
      return t("checklist.ai.info.purpose");
    case "implementation":
      return t("checklist.ai.info.implementation");
    case "challenges":
      return t("checklist.ai.info.challenges");
    case "bestPractices":
      return t("checklist.ai.info.bestPractices");
    default:
      return fallback;
  }
}

function InformationSectionCard({
  section,
  idx,
}: {
  section: InformationSection;
  idx: number;
}) {
  const title = useInfoSectionTitle(section.key, section.title);
  const config = INFO_SECTION_CONFIG[section.key];
  if (!config) return null;
  const SectionIcon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.25,
        delay: idx * 0.06,
        ease: "easeOut",
      }}
      className={`rounded-xl border border-border bg-page/60 border-l-[3px] ${config.borderColor} px-4 py-3.5`}
    >
      <div className="flex items-center gap-2.5">
        <div
          className={`flex h-7 w-7 items-center justify-center rounded-lg ${config.iconBg}`}
        >
          <SectionIcon size={14} className={config.iconColor} />
        </div>
        <SectionLabel>{title}</SectionLabel>
      </div>
      <p className="text-[13px] text-secondary leading-relaxed mt-2">
        {section.content}
      </p>
    </motion.div>
  );
}

function StructuredInformationCard({
  report,
}: {
  report: ChecklistInformationReport;
}) {
  return (
    <div className="space-y-2.5">
      {report.sections.map((section, idx) => (
        <InformationSectionCard key={section.key} section={section} idx={idx} />
      ))}
    </div>
  );
}

// ─── Structured Summary Card ─────────────────────────────────────────────────

function StructuredSummaryCard({ report }: { report: ChecklistSummaryReport }) {
  const { t } = useTranslation("audits");
  const [gapsExpanded, setGapsExpanded] = useState(false);

  const level = getComplianceLevel(report.complianceStatus);
  const config = COMPLIANCE_CONFIG[level];
  const StatusIcon = config.icon;
  const pct = Math.round(report.confidence * 100);
  const hasGaps = report.gaps.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="rounded-xl border border-border bg-page/60 overflow-hidden divide-y divide-border/30"
    >
      {/* Status Banner */}
      <div
        className={`flex items-center justify-between px-4 py-3 ${config.bannerBg} ${config.bannerBorder} border-b`}
      >
        <div className="flex items-center gap-2.5">
          <StatusIcon size={18} className={config.iconColor} />
          <span className={`text-[13px] font-semibold ${config.textColor}`}>
            {report.complianceStatus}
          </span>
          <span
            className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
              report.recommendation === "Approve"
                ? "bg-green-500/15 text-green-700"
                : "bg-amber-500/15 text-amber-700"
            }`}
          >
            {report.recommendation === "Approve"
              ? t("checklist.ai.approve")
              : t("checklist.ai.requestChanges")}
          </span>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-[13px] font-mono font-medium text-primary tabular-nums">
            {pct}%
          </span>
          <div className="w-16 h-1 rounded-full bg-black/5 overflow-hidden">
            <div
              className={`h-full rounded-full ${config.barColor} transition-all`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Verdict */}
      <div className="px-4 py-3">
        <SectionLabel>{t("checklist.ai.verdict")}</SectionLabel>
        <p className="text-[14px] font-medium text-primary mt-1 border-l-2 border-accent pl-3">
          {report.verdict}
        </p>
      </div>

      {/* Key Findings */}
      <div className="px-4 py-3">
        <SectionLabel>{t("checklist.ai.keyFindings")}</SectionLabel>
        <div className="mt-2 space-y-2">
          {report.keyFindings.map((kf, idx) => {
            const sentimentCfg = SENTIMENT_CONFIG[kf.sentiment];
            const SentimentIcon = sentimentCfg.icon;
            return (
              <motion.div
                key={kf.finding}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05, duration: 0.2 }}
                className="flex items-start gap-2"
              >
                <SentimentIcon
                  size={14}
                  className={`${sentimentCfg.color} mt-0.5 shrink-0`}
                />
                <span className="text-[13px] text-secondary leading-relaxed">
                  {kf.finding}
                </span>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Gaps (collapsible) */}
      {hasGaps && (
        <div className="px-4 py-3">
          <button
            type="button"
            onClick={() => setGapsExpanded((prev) => !prev)}
            className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted hover:text-primary transition-colors"
          >
            <motion.div
              animate={{ rotate: gapsExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown size={12} />
            </motion.div>
            {t("checklist.ai.gaps")} —{" "}
            {t("checklist.ai.gapsCount", { count: report.gaps.length })}
          </button>
          <AnimatePresence initial={false}>
            {gapsExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <ul className="mt-2 space-y-1.5 pl-1">
                  {report.gaps.map((gap) => (
                    <li
                      key={gap}
                      className="flex items-start gap-2 text-[13px] text-secondary leading-relaxed"
                    >
                      <span className="text-red-500 mt-1 shrink-0">•</span>
                      {gap}
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Next Steps */}
      {report.nextSteps.length > 0 && (
        <div className="px-4 py-3">
          <SectionLabel>{t("checklist.ai.nextSteps")}</SectionLabel>
          <div className="mt-2 space-y-1.5">
            {report.nextSteps.map((step, idx) => (
              <div key={step} className="flex items-start gap-2">
                <span className="text-[11px] font-mono font-medium text-muted mt-0.5 shrink-0 w-4 text-right">
                  {idx + 1}
                </span>
                <ArrowRight size={12} className="text-muted mt-1 shrink-0" />
                <span className="text-[13px] text-secondary leading-relaxed">
                  {step}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Evidence Sources */}
      {report.evidenceSources.length > 0 && (
        <div className="px-4 py-3">
          <SectionLabel>{t("checklist.ai.evidenceSources")}</SectionLabel>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {report.evidenceSources.map((source) => (
              <span
                key={source}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-muted bg-card border border-border/50 rounded-md px-2 py-0.5"
              >
                <FileText size={10} className="shrink-0" />
                {source}
              </span>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ─── Enrichment In Progress Banner ────────────────────────────────────────────

function EnrichmentInProgressBanner() {
  const { t } = useTranslation("audits");
  return (
    <div className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2.5 flex items-start gap-2.5">
      <Loader2 size={16} className="text-accent animate-spin shrink-0 mt-0.5" />
      <div className="flex flex-col gap-0.5">
        <p className="text-[12px] font-medium text-accent leading-tight">
          {t("findings.section.enrichmentInProgress")}
        </p>
        <p className="text-[11px] text-muted leading-snug">
          {t("findings.section.enrichmentInProgressDetail")}
        </p>
      </div>
    </div>
  );
}

function SummaryRefreshingBanner() {
  const { t } = useTranslation("audits");

  return (
    <div className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2.5 flex items-start gap-2.5">
      <Loader2 size={16} className="text-accent animate-spin shrink-0 mt-0.5" />
      <div className="flex flex-col gap-0.5">
        <p className="text-[12px] font-medium text-accent leading-tight">
          {t("checklist.ai.generatingSummary")}
        </p>
        <p className="text-[11px] text-muted leading-snug">
          {t("checklist.ai.summaryPreviewPending")}
        </p>
      </div>
    </div>
  );
}

// ─── Summary Section ──────────────────────────────────────────────────────────

export function AISummarySection({
  item,
  onGenerateSummary,
  onGenerateEverything,
}: {
  item: UIChecklistItem;
  onGenerateSummary?: () => void;
  onGenerateEverything?: () => void;
}) {
  const { t } = useTranslation("audits");
  const hasSummaryReport = !!item.summaryReport;
  const hasFullPipelineContext = hasChecklistWorkflowContext(item);
  const enrichmentInProgress =
    item.summaryStatus === "completed" &&
    item.findingsGenerationStatus === "generating";
  const showWorkflowCard = shouldShowChecklistWorkflowCard(item);
  const shouldKeepSummaryVisibleDuringPipeline =
    hasFullPipelineContext && hasSummaryReport;
  const showSummaryRefreshingBanner =
    item.summaryStatus === "pending" && shouldKeepSummaryVisibleDuringPipeline;
  const canRerunEverything =
    !!onGenerateEverything && hasFullPipelineContext && !showWorkflowCard;
  const hasLinkedDocs = (item.linkedDocumentIds?.length ?? 0) > 0;
  const questionCount = item.questions?.length ?? 0;
  const answeredCount = item.answers?.length ?? 0;
  const hasUnansweredQuestions =
    questionCount > 0 && answeredCount < questionCount;
  const showGenerateEverything =
    !!onGenerateEverything && hasLinkedDocs && hasUnansweredQuestions;

  if (
    item.summaryStatus === "pending" &&
    !shouldKeepSummaryVisibleDuringPipeline
  ) {
    if (showWorkflowCard) {
      return (
        <ChecklistGenerationWorkflowCard
          item={item}
          onGenerateEverything={onGenerateEverything}
        />
      );
    }
    return <SkeletonLoader label={t("checklist.ai.generatingSummary")} />;
  }

  if (item.summaryStatus === "failed") {
    if (showWorkflowCard) {
      return (
        <ChecklistGenerationWorkflowCard
          item={item}
          onGenerateEverything={onGenerateEverything}
        />
      );
    }
    return (
      <div className="flex flex-col items-center text-center py-8 px-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-destructive/10 mb-3.5">
          <AlertTriangle size={20} className="text-destructive" />
        </div>
        <p className="text-[13px] text-secondary leading-relaxed mb-5 max-w-[240px]">
          {t("checklist.ai.summaryFailed")}
        </p>
        <Button
          variant="primary"
          size="sm"
          icon={RefreshCw}
          onClick={onGenerateSummary}
          disabled={!onGenerateSummary}
        >
          {t("checklist.ai.retryGeneration")}
        </Button>
      </div>
    );
  }

  if (!item.summaryReport) {
    return (
      <div className="flex flex-col items-center text-center py-8 px-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-accent-soft mb-3.5">
          <Sparkles size={20} className="text-accent" />
        </div>
        {showGenerateEverything ? (
          <>
            <p className="text-[13px] text-secondary leading-relaxed mb-5 max-w-[260px]">
              {t("checklist.ai.generateEverythingDescription")}
            </p>
            <div className="flex flex-col items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                icon={Sparkles}
                onClick={onGenerateEverything}
                className="border border-green-500/60 hover:border-green-500"
              >
                {t("checklist.ai.generateEverything")}
              </Button>
              {onGenerateSummary && (
                <button
                  type="button"
                  onClick={onGenerateSummary}
                  className="text-[11px] text-muted hover:text-secondary underline underline-offset-2"
                >
                  {t("checklist.ai.generateSummary")}
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="text-[13px] text-secondary leading-relaxed mb-5 max-w-[240px]">
              {t("checklist.ai.generateSummaryDescription")}
            </p>
            <Button
              variant="primary"
              size="sm"
              icon={Sparkles}
              onClick={onGenerateSummary}
              disabled={!onGenerateSummary}
              className="border border-green-500/60 hover:border-green-500"
            >
              {t("checklist.ai.generateSummary")}
            </Button>
          </>
        )}
      </div>
    );
  }

  const parsed = parseSummaryReport(item.summaryReport);

  if (typeof parsed === "string" || parsed === null) {
    return (
      <div className="space-y-3">
        {showWorkflowCard && (
          <ChecklistGenerationWorkflowCard
            item={item}
            onGenerateEverything={onGenerateEverything}
          />
        )}
        {showSummaryRefreshingBanner && <SummaryRefreshingBanner />}
        {enrichmentInProgress && <EnrichmentInProgressBanner />}
        <div className="rounded-xl border border-border bg-page/60 px-4 py-3.5">
          <div className="border-l-2 border-accent/50 pl-3">
            <p className="text-[13px] text-secondary leading-relaxed">
              {item.summaryReport}
            </p>
          </div>
        </div>
        {onGenerateSummary && (
          <Button
            variant="accent-soft"
            size="sm"
            icon={RefreshCw}
            onClick={onGenerateSummary}
            className="border border-green-500/60 hover:border-green-500"
          >
            {t("checklist.ai.regenerate")}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 pb-4">
      {showWorkflowCard && (
        <ChecklistGenerationWorkflowCard
          item={item}
          onGenerateEverything={onGenerateEverything}
        />
      )}
      {showSummaryRefreshingBanner && <SummaryRefreshingBanner />}
      {enrichmentInProgress && <EnrichmentInProgressBanner />}
      {canRerunEverything && (
        <div className="flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            icon={Sparkles}
            onClick={onGenerateEverything}
          >
            {t("checklist.ai.workflow.rerunEverything")}
          </Button>
        </div>
      )}
      {onGenerateSummary && (
        <div className="flex justify-end">
          <Button
            variant="accent-soft"
            size="sm"
            icon={RefreshCw}
            onClick={onGenerateSummary}
            className="border border-accent/30 hover:border-accent/60"
          >
            {t("checklist.ai.regenerate")}
          </Button>
        </div>
      )}
      <StructuredSummaryCard report={parsed} />
    </div>
  );
}

// ─── Information Content ──────────────────────────────────────────────────────

export function AIInformationContent({
  item,
  onGenerateInformation,
}: {
  item: UIChecklistItem;
  onGenerateInformation?: (instructions: string) => void;
}) {
  const { t } = useTranslation("audits");
  const infoPending = item.informationStatus === "pending";

  return (
    <AnimatePresence mode="wait">
      {infoPending ? (
        <motion.div
          key="info-pending"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <SkeletonLoader label={t("checklist.ai.generatingInformation")} />
        </motion.div>
      ) : item.informationReport ? (
        <motion.div
          key="info-content"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="space-y-2"
        >
          {onGenerateInformation && (
            <div className="flex justify-end">
              <AIGeneratePopover
                onGenerate={onGenerateInformation}
                label={t("checklist.ai.regenerate")}
              >
                <Button
                  variant="accent-soft"
                  size="sm"
                  icon={RefreshCw}
                  className="border border-accent/30 hover:border-accent/60"
                >
                  {t("checklist.ai.regenerate")}
                </Button>
              </AIGeneratePopover>
            </div>
          )}
          {(() => {
            const parsed = parseInformationReport(item.informationReport);
            if (typeof parsed === "string") {
              return (
                <div className="rounded-xl border border-border bg-page/60 px-4 py-3.5">
                  <div className="prose-chat text-[13px] text-secondary leading-relaxed">
                    <Markdown remarkPlugins={[remarkGfm]}>{parsed}</Markdown>
                  </div>
                </div>
              );
            }
            return <StructuredInformationCard report={parsed} />;
          })()}
        </motion.div>
      ) : (
        <motion.div
          key="info-empty"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="rounded-xl border border-dashed border-border/60 px-5 py-8 flex flex-col items-center text-center gap-3"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft">
            <Info size={16} className="text-accent" />
          </div>
          <div className="space-y-1">
            <p className="text-[13px] font-medium text-secondary">
              {t("checklist.ai.noInformation")}
            </p>
            <p className="text-[12px] text-muted leading-relaxed max-w-[260px]">
              {t("checklist.ai.noInformationDescription")}
            </p>
          </div>
          {onGenerateInformation && (
            <AIGeneratePopover
              onGenerate={onGenerateInformation}
              label={t("checklist.ai.generateInformation")}
            >
              <Button
                variant="accent-soft"
                size="sm"
                icon={Sparkles}
                className="border border-accent/30 hover:border-accent/60"
              >
                {t("checklist.ai.generateInformation")}
              </Button>
            </AIGeneratePopover>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Examples Content ─────────────────────────────────────────────────────────

export function AIExamplesContent({
  item,
  onGenerateExamples,
}: {
  item: UIChecklistItem;
  onGenerateExamples?: (instructions: string) => void;
}) {
  const { t } = useTranslation("audits");
  const examples = item.exampleDocuments ?? [];
  const examplesPending = item.exampleDocumentsStatus === "pending";

  return (
    <AnimatePresence mode="wait">
      {examplesPending ? (
        <motion.div
          key="ex-pending"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <SkeletonLoader label={t("checklist.ai.generatingExamples")} />
        </motion.div>
      ) : examples.length > 0 ? (
        <motion.div
          key="ex-content"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="space-y-2"
        >
          {onGenerateExamples && (
            <div className="flex justify-end">
              <AIGeneratePopover
                onGenerate={onGenerateExamples}
                label={t("checklist.ai.regenerate")}
              >
                <Button
                  variant="accent-soft"
                  size="sm"
                  icon={RefreshCw}
                  className="border border-accent/30 hover:border-accent/60"
                >
                  {t("checklist.ai.regenerate")}
                </Button>
              </AIGeneratePopover>
            </div>
          )}
          <div className="space-y-2.5">
            {examples.map((example, idx) => (
              <div
                key={example.name}
                className="rounded-xl border border-border bg-page/60 overflow-hidden"
              >
                <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-border/50 bg-card">
                  <span className="text-[10px] font-bold text-muted/50 tabular-nums">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <p className="text-[12px] font-semibold text-primary flex-1 min-w-0 truncate">
                    {example.name}
                  </p>
                </div>
                <div className="px-4 py-3">
                  <div className="prose-chat text-[13px] text-secondary leading-relaxed">
                    <Markdown remarkPlugins={[remarkGfm]}>
                      {example.content}
                    </Markdown>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="ex-empty"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="rounded-xl border border-dashed border-border/60 px-5 py-8 flex flex-col items-center text-center gap-3"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft">
            <Sparkles size={16} className="text-accent" />
          </div>
          <div className="space-y-1">
            <p className="text-[13px] font-medium text-secondary">
              {t("checklist.ai.noExamples")}
            </p>
            <p className="text-[12px] text-muted leading-relaxed max-w-[260px]">
              {t("checklist.ai.noExamplesDescription")}
            </p>
          </div>
          {onGenerateExamples && (
            <AIGeneratePopover
              onGenerate={onGenerateExamples}
              label={t("checklist.ai.examples")}
            >
              <Button
                variant="accent-soft"
                size="sm"
                icon={Sparkles}
                className="border border-accent/30 hover:border-accent/60"
              >
                {t("checklist.ai.examples")}
              </Button>
            </AIGeneratePopover>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
